import { createContext, useContext, useReducer, useCallback, useEffect, type ReactNode } from 'react'
import { recordsApi, type ActivityRecord, timersApi } from '@/lib/api'
import { storage, type TimerSnapshot } from '@/store/storage'
import type { Category } from '@/lib/constants'

interface RecordState {
  records: ActivityRecord[]
  todayRecords: ActivityRecord[]
  activeTimer: TimerSnapshot | null
  loading: boolean
  syncing: boolean
  offline: boolean
}

type RecordAction =
  | { type: 'SET_RECORDS'; payload: ActivityRecord[] }
  | { type: 'SET_TODAY_RECORDS'; payload: ActivityRecord[] }
  | { type: 'START_TIMER'; payload: TimerSnapshot }
  | { type: 'STOP_TIMER' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_SYNCING'; payload: boolean }
  | { type: 'SET_OFFLINE'; payload: boolean }
  | { type: 'ADD_RECORD'; payload: ActivityRecord }
  | { type: 'REMOVE_RECORD'; payload: number }

const initialState: RecordState = {
  records: [],
  todayRecords: [],
  activeTimer: storage.getTimerSnapshot(),
  loading: false,
  syncing: false,
  offline: !navigator.onLine,
}

function recordReducer(state: RecordState, action: RecordAction): RecordState {
  switch (action.type) {
    case 'SET_RECORDS':
      return { ...state, records: action.payload }
    case 'SET_TODAY_RECORDS':
      return { ...state, todayRecords: action.payload }
    case 'START_TIMER':
      storage.setTimerSnapshot(action.payload)
      return { ...state, activeTimer: action.payload }
    case 'STOP_TIMER':
      storage.setTimerSnapshot(null)
      return { ...state, activeTimer: null }
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_SYNCING':
      return { ...state, syncing: action.payload }
    case 'SET_OFFLINE':
      return { ...state, offline: action.payload }
    case 'ADD_RECORD':
      return {
        ...state,
        todayRecords: [action.payload, ...state.todayRecords],
        records: [action.payload, ...state.records],
      }
    case 'REMOVE_RECORD':
      return {
        ...state,
        todayRecords: state.todayRecords.filter(r => r.id !== action.payload),
        records: state.records.filter(r => r.id !== action.payload),
      }
    default:
      return state
  }
}

interface RecordContextValue extends RecordState {
  startTimer: (category: Category) => Promise<void>
  stopTimer: (details?: Record<string, string>) => Promise<ActivityRecord | null>
  discardTimer: () => Promise<void>
  restoreTimer: (snapshot: TimerSnapshot) => void
  fetchTodayRecords: () => Promise<void>
  deleteRecord: (id: number) => Promise<void>
  syncOfflineRecords: () => Promise<void>
}

const RecordContext = createContext<RecordContextValue | null>(null)

export function RecordProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(recordReducer, initialState)

  useEffect(() => {
    const goOnline = () => dispatch({ type: 'SET_OFFLINE', payload: false })
    const goOffline = () => dispatch({ type: 'SET_OFFLINE', payload: true })
    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    }
  }, [])

  useEffect(() => {
    if (!state.offline) {
      syncOfflineRecords()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.offline])

  const fetchTodayRecords = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true })
    try {
      const today = new Date()
      // UTC dates for consistent timezone handling
      const utcStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()))
      const utcEnd = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() + 1))
      
      const startDate = `${utcStart.getUTCFullYear()}-${(utcStart.getUTCMonth() + 1).toString().padStart(2, '0')}-${utcStart.getUTCDate().toString().padStart(2, '0')}`
      const endDate = `${utcEnd.getUTCFullYear()}-${(utcEnd.getUTCMonth() + 1).toString().padStart(2, '0')}-${utcEnd.getUTCDate().toString().padStart(2, '0')}`
      
      const res = await recordsApi.get({ startDate, endDate })
      const records = (res as unknown as { data: ActivityRecord[] }).data || []
      dispatch({ type: 'SET_TODAY_RECORDS', payload: records })
    } catch (e) {
      console.error('Failed to fetch records:', e)
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [])

  const startTimer = useCallback(async (category: Category) => {
    const snapshot: TimerSnapshot = { category, startTime: Date.now() }
    dispatch({ type: 'START_TIMER', payload: snapshot })
    try {
      await timersApi.start({ category, start_time: snapshot.startTime })
    } catch (e) {
      console.error('Failed to sync timer start:', e)
    }
  }, [])

  const stopTimer = useCallback(async (details?: Record<string, string>): Promise<ActivityRecord | null> => {
    const timer = state.activeTimer
    if (!timer) return null

    const endTime = Date.now()
    const duration = Math.floor((endTime - timer.startTime) / 1000)

    // 检查时长是否少于10秒
    if (duration < 10) {
      alert('记录时长少于10秒，不会被保存。')
      dispatch({ type: 'STOP_TIMER' })
      try {
        await timersApi.stop()
      } catch (e) {
        console.error('Failed to discard short timer:', e)
      }
      return null
    }

    const recordData = {
      category: timer.category,
      start_time: timer.startTime,
      end_time: endTime,
      duration,
      details: details || undefined,
    }

    dispatch({ type: 'STOP_TIMER' })

    try {
      await timersApi.stop()
      if (navigator.onLine) {
        const res = await recordsApi.create(recordData)
        const record = (res as unknown as { data: ActivityRecord }).data
        dispatch({ type: 'ADD_RECORD', payload: record })
        return record
      } else {
        // Offline: create temporary record for UI, add to offline queue
        const tempRecord: ActivityRecord = {
          id: -Date.now(),
          user_id: -1,
          category: recordData.category,
          start_time: recordData.start_time,
          end_time: recordData.end_time,
          duration: recordData.duration,
          is_auto_closed: false,
          details: recordData.details || null,
          created_at: new Date().toISOString(),
        }
        dispatch({ type: 'ADD_RECORD', payload: tempRecord })
        storage.addToOfflineQueue({ ...recordData, timestamp: Date.now() })
        return tempRecord
      }
    } catch (e) {
      console.error('Failed to save record:', e)
      // Online but request failed: also create temporary record
      const tempRecord: ActivityRecord = {
        id: -Date.now(),
        user_id: -1,
        category: recordData.category,
        start_time: recordData.start_time,
        end_time: recordData.end_time,
        duration: recordData.duration,
        is_auto_closed: false,
        details: recordData.details || null,
        created_at: new Date().toISOString(),
      }
      dispatch({ type: 'ADD_RECORD', payload: tempRecord })
      storage.addToOfflineQueue({ ...recordData, timestamp: Date.now() })
      return tempRecord
    }
  }, [state.activeTimer])

  const discardTimer = useCallback(async (): Promise<void> => {
    const timer = state.activeTimer
    if (!timer) return

    dispatch({ type: 'STOP_TIMER' })

    try {
      await timersApi.stop()
    } catch (e) {
      console.error('Failed to discard timer:', e)
    }
  }, [state.activeTimer])

  const restoreTimer = useCallback((snapshot: TimerSnapshot) => {
    dispatch({ type: 'START_TIMER', payload: snapshot })
  }, [])

  const deleteRecord = useCallback(async (id: number) => {
    try {
      await recordsApi.delete(id)
      dispatch({ type: 'REMOVE_RECORD', payload: id })
    } catch (e) {
      console.error('Failed to delete record:', e)
    }
  }, [])

  const syncOfflineRecords = useCallback(async () => {
    const queue = storage.getOfflineQueue()
    if (queue.length === 0) return

    dispatch({ type: 'SET_SYNCING', payload: true })
    try {
      const records = queue.map(r => ({
        category: r.category,
        start_time: r.start_time,
        end_time: r.end_time,
        duration: r.duration,
        details: r.details,
      }))
      await recordsApi.createBatch(records)
      storage.clearOfflineQueue()
      await fetchTodayRecords()
    } catch (e) {
      console.error('Failed to sync offline records:', e)
    } finally {
      dispatch({ type: 'SET_SYNCING', payload: false })
    }
  }, [fetchTodayRecords])

  return (
    <RecordContext.Provider value={{
      ...state,
      startTimer,
      stopTimer,
      discardTimer,
      restoreTimer,
      fetchTodayRecords,
      deleteRecord,
      syncOfflineRecords,
    }}>
      {children}
    </RecordContext.Provider>
  )
}

export function useRecords() {
  const ctx = useContext(RecordContext)
  if (!ctx) throw new Error('useRecords must be used within RecordProvider')
  return ctx
}
